<template>
  <div v-if="visible" class="member-form-overlay" @click="handleOverlayClick">
    <div class="member-form-container" @click.stop>
      <div class="form-header">
        <h3>{{ isEdit ? '修改会员' : '添加会员' }}</h3>
        <button class="close-button" @click="close">×</button>
      </div>

      <form class="member-form" @submit.prevent="handleSubmit">
        <div class="form-group">
          <label class="form-label">会员姓名 *</label>
          <input
            v-model="formData.name"
            type="text"
            class="form-input"
            placeholder="请输入会员姓名"
            :class="{ 'error': errors.name }"
            @blur="validateName"
          />
          <span v-if="errors.name" class="error-message">{{ errors.name }}</span>
        </div>

        <div class="form-group">
          <label class="form-label">手机号 *</label>
          <input
            v-model="formData.phone"
            type="tel"
            class="form-input"
            placeholder="请输入手机号"
            :class="{ 'error': errors.phone }"
            @blur="validatePhone"
          />
          <span v-if="errors.phone" class="error-message">{{ errors.phone }}</span>
        </div>

        <div class="form-group">
          <label class="form-label">性别</label>
          <div class="radio-group">
            <label class="radio-label">
              <input
                v-model="formData.gender"
                type="radio"
                value="男"
                class="radio-input"
              />
              <span class="radio-text">男</span>
            </label>
            <label class="radio-label">
              <input
                v-model="formData.gender"
                type="radio"
                value="女"
                class="radio-input"
              />
              <span class="radio-text">女</span>
            </label>
            <label class="radio-label">
              <input
                v-model="formData.gender"
                type="radio"
                value=""
                class="radio-input"
              />
              <span class="radio-text">未知</span>
            </label>
          </div>
        </div>

        <div class="form-group">
          <label class="form-label">地址</label>
          <textarea
            v-model="formData.address"
            class="form-textarea"
            placeholder="请输入会员地址"
            rows="3"
          ></textarea>
        </div>

        <div v-if="isEdit" class="form-group">
          <label class="form-label">注册时间</label>
          <input
            :value="formData.registerDate"
            type="text"
            class="form-input"
            disabled
          />
        </div>

        <div class="form-actions">
          <button type="button" class="cancel-button" @click="close">取消</button>
          <button type="submit" class="submit-button" :disabled="isSubmitting">
            {{ isSubmitting ? '保存中...' : (isEdit ? '更新' : '添加') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MemberForm',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    member: {
      type: Object,
      default: null
    }
  },
  emits: ['close', 'submit'],
  data() {
    return {
      isSubmitting: false,
      formData: {
        name: '',
        phone: '',
        gender: '',
        address: '',
        registerDate: ''
      },
      errors: {
        name: '',
        phone: ''
      }
    }
  },
  computed: {
    isEdit() {
      return this.member !== null
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.resetForm()
        if (this.member) {
          this.formData = {
            id: this.member.id,
            name: this.member.name,
            phone: this.member.phone,
            gender: this.member.gender || '',
            address: this.member.address || '',
            registerDate: this.member.registerDate
          }
        }
      }
    }
  },
  methods: {
    resetForm() {
      this.formData = {
        name: '',
        phone: '',
        gender: '',
        address: '',
        registerDate: new Date().toISOString().split('T')[0]
      }
      this.errors = {
        name: '',
        phone: ''
      }
    },
    validateName() {
      if (!this.formData.name.trim()) {
        this.errors.name = '请输入会员姓名'
        return false
      }
      if (this.formData.name.trim().length < 2) {
        this.errors.name = '会员姓名至少需要2个字符'
        return false
      }
      this.errors.name = ''
      return true
    },
    validatePhone() {
      const phoneRegex = /^1[3-9]\d{9}$/
      if (!this.formData.phone.trim()) {
        this.errors.phone = '请输入手机号'
        return false
      }
      if (!phoneRegex.test(this.formData.phone)) {
        this.errors.phone = '请输入正确的手机号格式'
        return false
      }
      this.errors.phone = ''
      return true
    },
    validateForm() {
      const nameValid = this.validateName()
      const phoneValid = this.validatePhone()
      return nameValid && phoneValid
    },
    async handleSubmit() {
      if (!this.validateForm()) {
        return
      }

      this.isSubmitting = true

      try {
        // 模拟API调用延迟
        await new Promise(resolve => setTimeout(resolve, 500))

        const submitData = {
          ...this.formData,
          name: this.formData.name.trim(),
          phone: this.formData.phone.trim(),
          gender: this.formData.gender || '未知',
          address: this.formData.address.trim()
        }

        if (!this.isEdit) {
          // 新增会员时添加其他必要字段
          submitData.id = Date.now() // 临时生成ID
          submitData.orderCount = 0
          submitData.orderAmount = 0
        }

        this.$emit('submit', submitData)
        this.close()
      } catch (error) {
        console.error('保存会员失败:', error)
        alert('保存失败，请重试')
      } finally {
        this.isSubmitting = false
      }
    },
    close() {
      this.$emit('close')
    },
    handleOverlayClick() {
      this.close()
    }
  }
}
</script>

<style scoped>
.member-form-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.member-form-container {
  background-color: white;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.form-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 20px;
  border-bottom: 1px solid #f0f0f0;
}

.form-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.close-button {
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s;
}

.close-button:hover {
  background-color: #f5f5f5;
  color: #666;
}

.member-form {
  padding: 24px;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.form-input {
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  font-size: 14px;
  outline: none;
  transition: all 0.2s;
  box-sizing: border-box;
}

.form-input:focus {
  border-color: #1890ff;
  box-shadow: 0 2px 8px rgba(24, 144, 255, 0.1);
}

.form-input.error {
  border-color: #f5222d;
}

.form-input:disabled {
  background-color: #f5f5f5;
  color: #999;
  cursor: not-allowed;
}

.form-textarea {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  font-size: 14px;
  outline: none;
  transition: all 0.2s;
  box-sizing: border-box;
  resize: vertical;
  font-family: inherit;
}

.form-textarea:focus {
  border-color: #1890ff;
  box-shadow: 0 2px 8px rgba(24, 144, 255, 0.1);
}

.radio-group {
  display: flex;
  gap: 20px;
  margin-top: 8px;
}

.radio-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 14px;
  color: #333;
}

.radio-input {
  margin-right: 6px;
  width: 16px;
  height: 16px;
  accent-color: #1890ff;
}

.radio-text {
  user-select: none;
}

.error-message {
  display: block;
  margin-top: 6px;
  font-size: 12px;
  color: #f5222d;
  line-height: 1.4;
}

.form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 32px;
  padding-top: 20px;
  border-top: 1px solid #f0f0f0;
}

.cancel-button, .submit-button {
  height: 36px;
  padding: 0 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid;
}

.cancel-button {
  background-color: white;
  color: #666;
  border-color: #d9d9d9;
}

.cancel-button:hover {
  border-color: #4096ff;
  color: #4096ff;
}

.submit-button {
  background-color: #1890ff;
  color: white;
  border-color: #1890ff;
}

.submit-button:hover:not(:disabled) {
  background-color: #4096ff;
  border-color: #4096ff;
}

.submit-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* 动画效果 */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.member-form-container {
  animation: fadeIn 0.2s ease-out;
}
</style>